import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { countAudienceApiV1CampaignsAudienceCountPost } from '#/shared/api/generated/communication/theCargoCommunication'
import type { CampaignAudienceQuery } from '#/shared/api/generated/communication/models'
import { formatNumber } from '#/shared/lib/format'

/**
 * How far back a rolling rule is replayed to size it.
 *
 * A trigger campaign has nobody in it yet, so a present-tense count of it is
 * always zero — the only honest way to size one is to ask what the rule would
 * have caught over a window that has already happened. The service words the
 * answer's heading to match ("Who this would have caught last month"), which
 * is why the heading is read back rather than written here.
 */
export const AUDIENCE_BACKTEST_DAYS = 30

/** The filter-builder keys `POST /audience/count` also understands. */
const QUERY_KEYS = [
  'stage',
  'statuses',
  'provider_ids',
  'min_age_days',
] as const

/**
 * The parts of a `filter_definition` the audience count can run. The builder's
 * fields come from `GET /campaigns/filter-fields` and can outgrow this list, so
 * anything unrecognised is dropped rather than sent — a rejected count would
 * leave the modal with no figure at all, which is worse than one that ignores
 * a chip the count cannot honour.
 */
export function toAudienceQuery(
  definition: Record<string, unknown>,
): CampaignAudienceQuery {
  const query: CampaignAudienceQuery = {}
  for (const key of QUERY_KEYS) {
    const value = definition[key]
    if (value == null) continue
    Object.assign(query, { [key]: value })
  }
  return query
}

export interface CampaignAudienceProjection {
  /** The service's own heading for the figures. */
  basis?: string
  /** "229 people · 214 with email · 198 with a mobile · 176 callable". */
  headline?: string
  /** The frequency cap, the sampling caveat, whoever is already enrolled. */
  notes: string[]
}

interface AudienceCountOptions {
  /** Only while the modal that reads it is open and its rule is settled. */
  enabled: boolean
  query: CampaignAudienceQuery
}

/**
 * `POST /campaigns/audience/count` — what the rule above the box would catch.
 *
 * The service returns counts as numbers and the heading as a string; the
 * sentences here only place those numbers into words, which is formatting
 * rather than arithmetic. Nothing is added up or subtracted.
 */
export function useCampaignAudienceCount({
  enabled,
  query,
}: AudienceCountOptions): CampaignAudienceProjection {
  const { t } = useTranslation()
  const result = useQuery({
    queryKey: ['/api/v1/campaigns/audience/count', query],
    queryFn: ({ signal }) =>
      countAudienceApiV1CampaignsAudienceCountPost(query, signal),
    enabled,
    // Enrolment moves under the modal; a reopened dialog should ask again.
    staleTime: 0,
    // A failed projection leaves the box stating the rule without a figure,
    // which is what it did before the count existed. No toast for that.
    retry: false,
  })

  return useMemo(() => {
    const count = result.data?.data
    if (count == null) return { notes: [] }
    const notes: string[] = []
    if (count.in_other_campaign != null && count.in_other_campaign > 0) {
      notes.push(
        t('campaigns.modal.audience.inOtherCampaign', {
          count: count.in_other_campaign,
          total: formatNumber(count.people),
        }),
      )
    }
    if (count.already_enrolled != null && count.already_enrolled > 0) {
      notes.push(
        t('campaigns.modal.audience.alreadyEnrolled', {
          count: count.already_enrolled,
        }),
      )
    }
    // `capped` says the channel counts are a sample of the first few thousand.
    // `people` stays exact, so the caveat belongs on the breakdown, not the total.
    if (count.capped === true) {
      notes.push(t('campaigns.modal.audience.capped'))
    }
    return {
      basis: count.basis ?? undefined,
      headline: t('campaigns.modal.audience.headline', {
        people: formatNumber(count.people),
        email: formatNumber(count.have_email),
        mobile: formatNumber(count.have_mobile),
        callable: formatNumber(count.callable),
      }),
      notes,
    }
  }, [result.data, t])
}
